import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const getStatusDisplay = (status: 'beta' | 'in-progress' | 'soon' | 'completed') => {
  switch (status) {
    case 'beta':
      return { text: 'Beta', badge: 'admin-badge admin-badge-status-beta' };
    case 'in-progress':
      return { text: 'In Progress', badge: 'admin-badge admin-badge-status-in-progress' };
    case 'soon':
      return { text: 'Soon', badge: 'admin-badge admin-badge-status-soon' };
    case 'completed':
      return { text: 'Completed', badge: 'admin-badge admin-badge-status-completed' };
    default:
      return { text: 'Completed', badge: 'admin-badge admin-badge-status-completed' };
  }
};

interface LabsProject {
  _id: string;
  title: string;
  description: string;
  content: string;
  keyFeatures?: string;
  whatMakesDifferent?: string;
  useCases?: string;
  status: 'beta' | 'in-progress' | 'soon' | 'completed';
  imageUrl?: string;
  featured?: boolean;
  superFeatured?: boolean;
  navLabel?: string;
  navUrl?: string;
  primaryButton?: {
    enabled: boolean;
    text: string;
    url: string;
  };
  secondaryButton?: {
    enabled: boolean;
    text: string;
    url: string;
  };
  views: number;
  likes: number;
  createdAt: string;
  updatedAt: string;
}

interface LabsFormData {
  title: string;
  description: string;
  content: string;
  keyFeatures?: string;
  whatMakesDifferent?: string;
  useCases?: string;
  status: 'beta' | 'in-progress' | 'soon' | 'completed';
  imageUrl: string;
  featured: boolean;
  superFeatured: boolean;
  navLabel: string;
  navUrl: string;
  primaryButton: {
    enabled: boolean;
    text: string;
    url: string;
  };
  secondaryButton: {
    enabled: boolean;
    text: string;
    url: string;
  };
}

interface LabsAdminProps {
  token: string;
}

function LabsAdmin({ token: propToken }: LabsAdminProps) {
  const [labsProjects, setLabsProjects] = useState<LabsProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState<LabsProject | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState<LabsFormData>({
    title: '',
    description: '',
    content: '',
    keyFeatures: '',
    whatMakesDifferent: '',
    useCases: '',
    status: 'completed',
    imageUrl: '',
    featured: false,
    superFeatured: false,
    navLabel: '',
    navUrl: '',
    primaryButton: {
      enabled: true,
      text: 'VIEW PROJECT',
      url: ''
    },
    secondaryButton: {
      enabled: false,
      text: 'READ MORE',
      url: ''
    }
  });

  // Auto-save draft to localStorage
  useEffect(() => {
    if (showForm && (formData.title || formData.content || formData.description)) {
      const draftKey = editingProject ? `labs_draft_${editingProject._id}` : 'labs_draft_new';
      localStorage.setItem(draftKey, JSON.stringify(formData));
    }
  }, [formData, showForm, editingProject]);

  // Clear draft from localStorage
  const clearDraft = () => {
    const draftKey = editingProject ? `labs_draft_${editingProject._id}` : 'labs_draft_new';
    localStorage.removeItem(draftKey);
  };

  // Reset form to default values
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      content: '',
      keyFeatures: '',
      whatMakesDifferent: '',
      useCases: '',
      status: 'completed',
      imageUrl: '',
      featured: false,
      superFeatured: false,
      navLabel: '',
      navUrl: '',
      primaryButton: {
        enabled: true,
        text: 'VIEW PROJECT',
        url: ''
      },
      secondaryButton: {
        enabled: false,
        text: 'READ MORE',
        url: ''
      }
    });
    setEditingProject(null);
  };

  // Fetch labs projects from API
  const fetchLabsProjects = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      
      // No need to get token - httpOnly cookies are sent automatically
      const response = await axios.get(`${apiUrl}/api/labs/admin/all`);
      
      setLabsProjects(response.data.data || []);
    } catch (error) {
      console.error('Error fetching labs projects:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLabsProjects();
  }, [fetchLabsProjects]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.superFeatured) {
      if (!formData.navLabel.trim() || !formData.navUrl.trim()) {
        alert('SUPER FAVORITE needs a nav label and redirect URL (header link on the public site).');
        return;
      }
    }

    setLoading(true);

    try {
      // No need to get token - httpOnly cookies are sent automatically
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;

      const payload = {
        title: formData.title,
        description: formData.description,
        content: formData.content,
        keyFeatures: formData.keyFeatures,
        whatMakesDifferent: formData.whatMakesDifferent,
        useCases: formData.useCases,
        status: formData.status,
        imageUrl: formData.imageUrl,
        featured: formData.featured,
        superFeatured: formData.superFeatured,
        navLabel: formData.superFeatured ? formData.navLabel.trim() : '',
        navUrl: formData.superFeatured ? formData.navUrl.trim() : '',
        primaryButton: formData.primaryButton,
        secondaryButton: formData.secondaryButton
      };

      if (editingProject) {
        await axios.put(
          `${apiUrl}/api/labs/${editingProject._id}`,
          payload
        );
      } else {
        await axios.post(
          `${apiUrl}/api/labs`,
          payload
        );
      }

      await fetchLabsProjects();
      clearDraft();
      resetForm();
      setShowForm(false);
      alert(editingProject ? 'Updated!' : 'Created!');
    } catch (error: any) {
      alert(`ERROR: ${error.response?.data?.message || error.response?.data?.error || error.message || 'Error saving project'}`);
    } finally {
      setLoading(false);
    }
  };

  // Convert local image URL to full API URL
  const convertToFullUrl = (imageUrl: string) => {
    if (!imageUrl) return '';
    
    // If it's already an external URL (http/https), return as-is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    // If it's a local API path like /api/images/blogs/filename, prepend backend URL
    if (imageUrl.startsWith('/api/images/')) {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      return `${apiUrl}${imageUrl}`;
    }
    
    // If it's old path format /uploads/..., extract filename and use new format
    if (imageUrl.includes('/uploads/')) {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const filename = imageUrl.split('/').pop() || '';
      return `${apiUrl}/api/images/blogs/${filename}`;
    }
    
    // If it's just a filename, use /api/images/blogs endpoint
    const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
    return `${apiUrl}/api/images/blogs/${imageUrl}`;
  };

  // Handle image upload
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const response = await axios.post(`${apiUrl}/api/upload/blog-image`, uploadFormData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          imageUrl: response.data.url
        }));
        alert('Image uploaded successfully!');
      } else {
        alert(`Upload failed: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  // Handle edit
  const handleEdit = (project: LabsProject) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      content: project.content,
      keyFeatures: project.keyFeatures || '',
      whatMakesDifferent: project.whatMakesDifferent || '',
      useCases: project.useCases || '',
      status: project.status,
      imageUrl: project.imageUrl || '',
      featured: project.featured === true ? true : false,
      superFeatured: project.superFeatured === true ? true : false,
      navLabel: project.navLabel || '',
      navUrl: project.navUrl || '',
      primaryButton: project.primaryButton || {
        enabled: true,
        text: 'VIEW PROJECT',
        url: ''
      },
      secondaryButton: project.secondaryButton || {
        enabled: false,
        text: 'READ MORE',
        url: ''
      }
    });
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this project?')) return;

    try {
      // No need to get token - httpOnly cookies are sent automatically
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      
      await axios.delete(`${apiUrl}/api/labs/${id}`);

      setLabsProjects(labsProjects.filter(p => p._id !== id));
      alert('Deleted!');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error deleting project');
    }
  };

  // Filter projects based on search
  const filteredProjects = labsProjects.filter(project =>
    project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="text-gray-100 font-sans h-full" style={{backgroundColor: '#0a0908', padding: '2rem'}}>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-dusk-amber to-dusk-copper bg-clip-text text-transparent mb-2">Labs Management</h1>
            <p className="text-gray-400">Create and manage DUSKPROOF projects and research</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50 focus:border-dusk-copper transition-all"
              />
              <svg 
                className="absolute left-3 top-3.5 h-5 w-5 text-[var(--dusk-faint)]" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            
            <button
              onClick={() => {
                if (showForm) {
                  setShowForm(false);
                } else {
                  resetForm();
                  setEditingProject(null);
                  setShowForm(true);
                }
              }}
              className="px-6 py-3 bg-gradient-to-r from-dusk-copper to-dusk-amber hover:from-dusk-copper-deep hover:to-dusk-copper text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-dusk-copper/40"
            >
              {showForm ? (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  + CREATE NEW LAB
                </>
              )}
            </button>
          </div>
        </div>

        {/* Add Project Form */}
        {showForm && (
          <div className="p-6 mb-8 rounded-2xl border border-[var(--dusk-line)] bg-[rgba(243,235,226,0.04)] backdrop-blur-sm">
            <h2 className="text-xl font-bold text-white mb-6">
              {editingProject ? 'Edit Labs Project' : 'Add New Labs Project'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Top Row: Title and Status */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                    placeholder="Project title"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value as any})}
                    className="w-full px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                  >
                    <option value="completed">✅ Completed</option>
                    <option value="beta">🧪 Beta</option>
                    <option value="in-progress">⚡ In Progress</option>
                    <option value="soon">🔜 Soon</option>
                  </select>
                </div>
              </div>

              {/* Favorite Checkbox - Full Width for Better Visibility */}
              <div className="bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg p-4 flex items-center gap-4">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => {
                    console.log('Favorite checkbox changed:', e.target.checked);
                    setFormData({...formData, featured: e.target.checked});
                  }}
                  className="w-6 h-6 cursor-pointer accent-dusk-copper"
                  id="favorite-checkbox"
                />
                <label htmlFor="favorite-checkbox" className="flex-1 cursor-pointer">
                  <span className="text-base font-bold text-white block">⭐ Mark This Project as FAVORITE</span>
                  <p className="text-xs text-gray-400 mt-1">It will appear in the "Professional Infrastructure Services" section on the homepage</p>
                </label>
              </div>

              {/* Super Favorite → site header nav */}
              <div className="bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-4">
                  <input
                    type="checkbox"
                    checked={formData.superFeatured}
                    onChange={(e) => {
                      setFormData({ ...formData, superFeatured: e.target.checked });
                    }}
                    className="w-6 h-6 cursor-pointer accent-dusk-copper"
                    id="super-favorite-checkbox"
                  />
                  <label htmlFor="super-favorite-checkbox" className="flex-1 cursor-pointer">
                    <span className="text-base font-bold text-white block">💎 Mark This Project as SUPER FAVORITE</span>
                    <p className="text-xs text-gray-400 mt-1">
                      Adds a link in the public site header next to Stake / Blog / Services
                    </p>
                  </label>
                </div>

                {formData.superFeatured && (
                  <div className="pt-2 border-t border-[var(--dusk-line)] space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Nav label (as shown on the site)
                        </label>
                        <input
                          type="text"
                          value={formData.navLabel}
                          onChange={(e) => setFormData({ ...formData, navLabel: e.target.value })}
                          maxLength={40}
                          className="w-full px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                          placeholder="e.g. Guard"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Nav link (redirect URL)
                        </label>
                        <input
                          type="url"
                          value={formData.navUrl}
                          onChange={(e) => setFormData({ ...formData, navUrl: e.target.value })}
                          className="w-full px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                          placeholder="https://guard.duskproof.com or /blog/…"
                        />
                      </div>
                    </div>

                    <div>
                      <p className="text-[0.65rem] font-semibold tracking-[0.14em] uppercase text-gray-500 mb-2">
                        Header preview
                      </p>
                      <div className="inline-flex flex-wrap items-center gap-0.5 rounded-full border border-[var(--dusk-line)] bg-[rgba(18,16,14,0.7)] px-1.5 py-1">
                        <span className="px-3.5 py-2 text-[0.72rem] font-semibold tracking-[0.1em] uppercase text-[var(--dusk-muted)]">
                          Stake
                        </span>
                        <span className="px-3.5 py-2 text-[0.72rem] font-semibold tracking-[0.1em] uppercase text-[var(--dusk-muted)]">
                          Blog
                        </span>
                        <span className="px-3.5 py-2 text-[0.72rem] font-semibold tracking-[0.1em] uppercase text-[var(--dusk-copper)]">
                          {formData.navLabel.trim() || 'Label'}
                        </span>
                        <span className="ml-1 px-4 py-2 text-[0.72rem] font-semibold tracking-[0.1em] uppercase rounded-full bg-[var(--dusk-copper)] text-white">
                          Services
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 m-0">
                        Front-end label: <strong className="text-gray-300">{formData.navLabel.trim() || '—'}</strong>
                        {formData.navUrl.trim() ? (
                          <>
                            {' '}
                            → <span className="text-gray-400 break-all">{formData.navUrl.trim()}</span>
                          </>
                        ) : (
                          <span className="text-amber-500"> — add a redirect URL</span>
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Project Description 
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                  placeholder="An intelligent analytics tool for tracking, classifying, and understanding blob activity."
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-300">Project Image</label>
                  {formData.imageUrl && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, imageUrl: '' })}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    className="flex-1 px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                    placeholder="Image URL"
                  />
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="flex items-center justify-center px-4 py-2 bg-dusk-copper hover:bg-dusk-copper-deep text-white rounded-lg cursor-pointer"
                    >
                      {uploading ? 'Uploading...' : 'Upload'}
                    </label>
                  </div>
                </div>
                {formData.imageUrl && (
                  <div className="mt-2">
                    <img 
                      src={convertToFullUrl(formData.imageUrl)}
                      alt="Preview" 
                      className="max-w-full h-32 object-cover rounded-lg border border-[var(--dusk-line-strong)]"
                      onError={(e) => {
                        console.error('Failed to load preview:', formData.imageUrl);
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Structured Content Fields */}
              <div className="border-t border-gray-700 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">📋 Project Details</h3>
                
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">📌 Key Features</label>
                    <textarea
                      value={formData.keyFeatures || ''}
                      onChange={(e) => setFormData({ ...formData, keyFeatures: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                      placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">🧠 What Makes It Different?</label>
                    <textarea
                      value={formData.whatMakesDifferent || ''}
                      onChange={(e) => setFormData({ ...formData, whatMakesDifferent: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                      placeholder="Describe unique selling points"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">🎯 Use Cases</label>
                    <textarea
                      value={formData.useCases || ''}
                      onChange={(e) => setFormData({ ...formData, useCases: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                      placeholder="Use case 1&#10;Use case 2&#10;Use case 3"
                    />
                  </div>
                </div>
              </div>

              {/* Button Configuration */}
              <div className="border-t border-gray-700 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">📌 Button Configuration</h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-[rgba(243,235,226,0.04)] rounded-lg p-4 border border-[var(--dusk-line-strong)]">
                    <label className="flex items-center gap-2 mb-3">
                      <input
                        type="checkbox"
                        checked={formData.primaryButton.enabled}
                        onChange={(e) => setFormData({
                          ...formData,
                          primaryButton: { ...formData.primaryButton, enabled: e.target.checked }
                        })}
                      />
                      <span className="text-sm text-white">Enable Primary Button</span>
                    </label>
                    {formData.primaryButton.enabled && (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={formData.primaryButton.text}
                          onChange={(e) => setFormData({
                            ...formData,
                            primaryButton: { ...formData.primaryButton, text: e.target.value }
                          })}
                          className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded text-white text-sm"
                          placeholder="Button Text"
                        />
                        <input
                          type="url"
                          value={formData.primaryButton.url}
                          onChange={(e) => setFormData({
                            ...formData,
                            primaryButton: { ...formData.primaryButton, url: e.target.value }
                          })}
                          className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded text-white text-sm"
                          placeholder="Button URL"
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-[rgba(243,235,226,0.04)] rounded-lg p-4 border border-[var(--dusk-line-strong)]">
                    <label className="flex items-center gap-2 mb-3">
                      <input
                        type="checkbox"
                        checked={formData.secondaryButton.enabled}
                        onChange={(e) => setFormData({
                          ...formData,
                          secondaryButton: { ...formData.secondaryButton, enabled: e.target.checked }
                        })}
                      />
                      <span className="text-sm text-white">Enable Secondary Button</span>
                    </label>
                    {formData.secondaryButton.enabled && (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={formData.secondaryButton.text}
                          onChange={(e) => setFormData({
                            ...formData,
                            secondaryButton: { ...formData.secondaryButton, text: e.target.value }
                          })}
                          className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded text-white text-sm"
                          placeholder="Button Text"
                        />
                        <input
                          type="url"
                          value={formData.secondaryButton.url}
                          onChange={(e) => setFormData({
                            ...formData,
                            secondaryButton: { ...formData.secondaryButton, url: e.target.value }
                          })}
                          className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded text-white text-sm"
                          placeholder="Button URL"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-6 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowForm(false);
                  }}
                  className="px-6 py-3 bg-[rgba(243,235,226,0.06)]/50 hover:bg-gray-600/50 text-gray-200 font-semibold rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 bg-gradient-to-r from-dusk-copper-deep to-dusk-copper hover:from-dusk-copper hover:to-dusk-amber text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-dusk-copper/30"
                >
                  {loading ? 'Saving...' : editingProject ? 'Update' : 'Add Project'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Projects Table */}
        <div className="rounded-2xl border border-[var(--dusk-line)] overflow-hidden bg-[rgba(243,235,226,0.03)] backdrop-blur-sm">
          <table className="w-full">
            <thead className="bg-[rgba(243,235,226,0.05)] border-b border-[var(--dusk-line)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Service</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--dusk-line)]">
              {loading ? (
                <tr className="hover:bg-[rgba(243,235,226,0.05)] transition-all">
                  <td colSpan={3} className="px-6 py-8 text-center text-gray-400">Loading...</td>
                </tr>
              ) : filteredProjects.length === 0 ? (
                <tr className="hover:bg-[rgba(243,235,226,0.05)] transition-all">
                  <td colSpan={3} className="px-6 py-8 text-center text-gray-400">No projects yet</td>
                </tr>
              ) : (
                filteredProjects.map((p) => {
                  const st = getStatusDisplay(p.status);
                  return (
                    <tr key={p._id} className="hover:bg-[rgba(243,235,226,0.05)] transition-all">
                      <td className="px-6 py-4">
                        <div className="flex gap-3 items-center">
                          {p.imageUrl && <img src={convertToFullUrl(p.imageUrl)} alt={p.title} className="h-10 w-10 rounded object-cover" />}
                          <div>
                            <div className="font-medium">{p.title}</div>
                            <div className="text-sm text-gray-400 truncate max-w-xs">{p.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={st.badge}>{st.text}</span>
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        <button onClick={() => handleEdit(p)} className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-all" title="Edit"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button>
                        <button onClick={() => handleDelete(p._id)} className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all" title="Delete"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default LabsAdmin;
